import Card from '@shared/uikit/card';
import { Loader } from '@shared/uikit/loader';
import { Pencil } from 'lucide-react';
import { formatNum } from '@app/utils/math';
import { daysUntilEndOfMonth, daysUntilEndOfQuarter, daysUntilEndOfYear } from '@app/utils/date';
import {
  useGetManagerMonthlyResult,
  useGetManagerQuarterlyResult,
  useGetManagerYearlyResult,
  useUpdateManagerMonthlyPlan,
  useUpdateManagerQuarterlyPlan,
  useUpdateManagerYearlyPlan,
} from '@entities/managers-perfomance';
import { useManagePlanResults } from './use-manage-plan-results';

import { ProgressBar } from './progress-bar';
import { EditMonthlyResult } from './edit-monthly-result';
import { EditQuarterlyResult } from './edit-quarterly-result';
import { EditYearlyResult } from './edit-yearly-result';

type ProgressCardType = 'monthly' | 'quarterly' | 'yearly';

type Props = {
  type: ProgressCardType;
  managerId: number | null;
  year: number;
  quarter?: number;
  canEditPlans: boolean;
};

export const ProgressCard = ({ type, managerId, year, quarter, canEditPlans }: Props) => {
  const currentMonth = new Date().getMonth() + 1;

  const query =
    type === 'monthly'
      ? useGetManagerMonthlyResult({ managerId, year, month: currentMonth })
      : type === 'quarterly'
        ? useGetManagerQuarterlyResult({ managerId, year, quarter: quarter ?? 1 })
        : useGetManagerYearlyResult({ managerId, year });

  const { data, isLoading } = query;

  const monthlyUpdate = useUpdateManagerMonthlyPlan();
  const quarterlyUpdate = useUpdateManagerQuarterlyPlan();
  const yearlyUpdate = useUpdateManagerYearlyPlan();

  const planData = { plan: data?.plan ?? undefined };

  const { isEditPopup, openPopup, closePopup, planValue, onUpdatePlan } = useManagePlanResults({
    type,
    data: planData,
    canEditPlans,
    monthlyUpdate,
    quarterlyUpdate,
    yearlyUpdate,
  });

  const value = data?.result != null ? formatNum(data.result) : '-';
  const plan = data?.plan != null ? formatNum(data.plan) : '-';
  const percent = data?.percentage;
  const displayPercent =
    percent !== undefined
      ? percent >= 100
        ? Math.round(percent).toString()
        : percent.toFixed(2)
      : null;

  const daysLeft =
    type === 'monthly'
      ? daysUntilEndOfMonth()
      : type === 'quarterly'
        ? daysUntilEndOfQuarter()
        : daysUntilEndOfYear();

  const title =
    type === 'monthly'
      ? 'Результат месячный'
      : type === 'quarterly'
        ? 'Результат квартальный'
        : 'Результат годовой';

  return (
    <>
      <Card
        header={
          <div className="flex items-center justify-between">
            <span className="max-w-[110px] text-sm text-gray-400">{title}</span>
            {!isLoading && (
              <div className="flex items-start gap-1">
                <span className="text-2xl text-white">{value}</span>
                {value !== '-' && <span className="text-sm text-gray-400">Ph</span>}
              </div>
            )}
          </div>
        }
        content={
          isLoading ? (
            <div className="h-[48px] flex items-center">
              <Loader size={45} loading />
            </div>
          ) : displayPercent !== null ? (
            <div className="flex flex-col gap-2">
              <div className="text-white/60 text-xs">{displayPercent}%</div>
              <ProgressBar percent={percent || 0} />
            </div>
          ) : null
        }
        footer={
          !isLoading && (
            <div className="flex justify-between items-center gap-2">
              <div className="flex items-center text-gray-400 gap-1 text-xs">
                <span>План: {plan} Ph</span>
              </div>
              <div className="flex items-center gap-2">
                {canEditPlans && (
                  <Pencil
                    size={16}
                    className="text-gray-400 cursor-pointer hover:opacity-70"
                    onClick={openPopup}
                  />
                )}
                <div className="inline-flex bg-blue-300/10 rounded-full py-1 px-[10px]">
                  <span className="text-blue-300 text-xs">{daysLeft} дн осталось</span>
                </div>
              </div>
            </div>
          )
        }
      />
      {type === 'monthly' && isEditPopup && (
        <EditMonthlyResult open value={planValue} onSubmit={onUpdatePlan} onClose={closePopup} />
      )}
      {type === 'quarterly' && isEditPopup && (
        <EditQuarterlyResult open value={planValue} onSubmit={onUpdatePlan} onClose={closePopup} />
      )}
      {type === 'yearly' && isEditPopup && (
        <EditYearlyResult open value={planValue} onSubmit={onUpdatePlan} onClose={closePopup} />
      )}
    </>
  );
};
